import { useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { ConfirmationModal, ModalControlModalProps } from '@frontend/design-system';

interface Props {
  modalProps: ModalControlModalProps;
  /** Deletes the saved draft. Resolves `true` once deletion is confirmed by the service. */
  onDiscard: () => Promise<boolean>;
}

/** Destructive confirmation for "Start fresh" (spec §5.4). Default focus lands on "Keep draft". */
export const DiscardDraftModal = ({ modalProps, onDiscard }: Props) => {
  const { t } = useTranslation('digital-forms-experience');
  const [isDiscarding, setIsDiscarding] = useState(false);
  const inFlightRef = useRef(false);

  const handleConfirm = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsDiscarding(true);
    try {
      await onDiscard();
    } finally {
      inFlightRef.current = false;
      setIsDiscarding(false);
    }
  };

  return (
    <ConfirmationModal
      {...modalProps}
      title={t('Discard saved progress?')}
      message={t('Your saved answers will be deleted. This cannot be undone.')}
      confirmLabel={isDiscarding ? t('Starting fresh…') : t('Discard and start fresh')}
      cancelLabel={t('Keep draft')}
      onConfirm={handleConfirm}
      destructive
    />
  );
};
